import { FC, ReactNode } from 'react';
import { IonChip, IonIcon, IonLabel } from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { createUseStyles } from 'react-jss';
import { LISTED_TAG_CHIP, TAG_CHIP_BORDER_WIDTH } from './TagChipMetrics';

const tagColorOptions = [
    '#4A8FA8', // Teal (primary theme equivalent)
    '#8E5FC7', // Purple (tertiary theme equivalent)
    '#D45A2A', // Orange (secondary theme equivalent)
    '#4A8F6B', // Green (success theme equivalent)
    '#5A72C7', // Blue
    '#C768A3', // Pink
    '#3E4A5C', // Dark blue-gray
    '#D47A47', // Orange-brown
    '#C73E3E'  // Red
];

// A tag's chip colours, the same for a tag wherever it shows.
const getTagColor = (tag: string) => {
    let hash = 0;
    for (let i = 0; i < tag.length; i++) {
        const char = tag.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    const colorIndex = Math.abs(hash) % tagColorOptions.length;
    const color = tagColorOptions[colorIndex];

    return {
        backgroundColor: `${color}1A`, // 10% opacity
        color: color,
        border: `${TAG_CHIP_BORDER_WIDTH}px solid ${color}`
    };
};

const useStyles = createUseStyles({
    '@keyframes highlightTag': {
        '0%': { boxShadow: '0 0 0 2px var(--ion-color-warning)', transform: 'scale(1.05)' },
        '100%': { boxShadow: '0 0 0 2px transparent', transform: 'scale(1)' }
    },

    listed: {
        flexShrink: 0,
        paddingLeft: LISTED_TAG_CHIP.paddingInline,
        paddingRight: LISTED_TAG_CHIP.paddingInline,
        marginLeft: LISTED_TAG_CHIP.marginInline,
        marginRight: LISTED_TAG_CHIP.marginInline,
        '& ion-label': {
            fontSize: 12,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        }
    },

    shrinksToEllipsis: {
        flexShrink: 1,
        minWidth: 0
    },

    chosen: {
        height: 32,
        borderRadius: 16,
        '& ion-label': {
            fontWeight: 500
        },
        '& ion-icon': {
            cursor: 'pointer',
            marginLeft: 4
        }
    },

    readOnly: {
        pointerEvents: 'none',
        cursor: 'default'
    },

    flashesAsDuplicate: {
        animation: '$highlightTag 0.6s ease-in-out'
    },

    suggested: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        borderRadius: 16,
        '& ion-label': {
            fontWeight: 500
        },
        '&:hover': {
            transform: 'translateY(-1px)',
            opacity: 0.8
        }
    },

    existing: {
        position: 'relative',
        '& ion-label': {
            fontSize: 14
        },
        '& strong': {
            fontWeight: 700,
            color: 'inherit'
        }
    },

    usageCount: {
        opacity: 0.7,
        fontSize: 12,
        fontWeight: 400
    }
});

const renderTagWithHighlight = ({ tag, matchedText }: { tag: string; matchedText: string; }): ReactNode => {
    if (!matchedText.trim()) {
        return tag;
    }

    const index = tag.toLowerCase().indexOf(matchedText.toLowerCase());
    if (index === -1) {
        return tag;
    }

    return (
        <>
            {tag.slice(0, index)}
            <strong>{tag.slice(index, index + matchedText.length)}</strong>
            {tag.slice(index + matchedText.length)}
        </>
    );
};

type ITagChipProps =
    // one of a list item's tags, in a row that fits them on one line
    | {
        kind: 'listed';
        tag: string;
        // gives way to an ellipsis when the row is too narrow for it
        shrinksToEllipsis?: boolean;
    }
    // a tag the item has; without onRemove it is read-only
    | {
        kind: 'chosen';
        tag: string;
        onRemove?: () => void;
        // flashes to point out the tag someone just tried to add again
        flashesAsDuplicate?: boolean;
    }
    // a tag offered for adding with a tap
    | {
        kind: 'suggested';
        tag: string;
        onSelect: () => void;
        // how many items on the care map already carry the tag
        usageCount?: number;
        // search text shown in bold where the tag contains it
        matchedText?: string;
    };

// Every tag chip, so a tag looks the same wherever it shows.
export const TagChip: FC<ITagChipProps> = props => {
    const classes = useStyles();
    const { tag } = props;

    if (props.kind === 'listed') {
        return (
            <IonChip
                className={props.shrinksToEllipsis ? `${classes.listed} ${classes.shrinksToEllipsis}` : classes.listed}
                style={getTagColor(tag)}
                data-type="tag-chip"
                data-tag-kind="listed"
            >
                <IonLabel>{tag}</IonLabel>
            </IonChip>
        );
    }

    if (props.kind === 'chosen') {
        const classNames = [classes.chosen];
        if (!props.onRemove) {
            classNames.push(classes.readOnly);
        }
        if (props.flashesAsDuplicate) {
            classNames.push(classes.flashesAsDuplicate);
        }
        return (
            <IonChip className={classNames.join(' ')} style={getTagColor(tag)} data-type="tag-chip" data-tag-kind="chosen">
                <IonLabel>{tag}</IonLabel>
                {props.onRemove && (
                    <IonIcon icon={closeOutline} onClick={props.onRemove} />
                )}
            </IonChip>
        );
    }

    const { usageCount, matchedText = '' } = props;
    return (
        <IonChip
            className={usageCount === undefined ? classes.suggested : `${classes.suggested} ${classes.existing}`}
            style={getTagColor(tag)}
            onClick={props.onSelect}
            data-type="tag-chip"
            data-tag-kind={usageCount === undefined ? 'suggested' : 'existing'}
        >
            {usageCount === undefined ? (
                <IonLabel>{tag}</IonLabel>
            ) : (
                <IonLabel>
                    {renderTagWithHighlight({ tag, matchedText })}
                    <span className={classes.usageCount} data-type="tag-count"> ({usageCount})</span>
                </IonLabel>
            )}
        </IonChip>
    );
};
